// Requires the server contract in docs/LEAD_INTEGRATION.md. No credentials belong in theme code.
const TAHHO_ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'ttclid', 'msclkid'];
function tahhoAttribution() {
  let saved = {};
  try { saved = JSON.parse(sessionStorage.getItem('tahho-attribution') || '{}'); } catch {}
  const params = new URLSearchParams(location.search);
  const value = {};
  for (const key of TAHHO_ATTRIBUTION_KEYS) {
    const candidate = saved[key] || params.get(key);
    if (typeof candidate === 'string') value[key] = candidate.slice(0, key.startsWith('utm_') ? 255 : 500);
  }
  value.landing_page = typeof saved.landing_page === 'string' ? saved.landing_page.split('?')[0].slice(0, 2000) : location.pathname;
  try { sessionStorage.setItem('tahho-attribution', JSON.stringify(value)); } catch {}
  return value;
}
class TahhoLeadFlow extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.campaign = this.dataset.campaign === 'true';
    this.inline = this.campaign || this.dataset.inline === 'true';
    this.root = this.inline ? this : this.closest('tahho-product, tahho-room-preview') || this.parentElement;
    this.dialog = this.inline ? null : this.querySelector('dialog');
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.followUpForm = this.querySelector('[data-follow-up-form]');
    if (this.campaign) this.querySelector('[data-submit]').disabled = false;
    this.attribution = tahhoAttribution();
    this.files = [];
    this.urls = [];
    this.root.addEventListener('click', event => {
      const trigger = event.target.closest('[data-lead]');
      if (trigger && !trigger.disabled) this.open(trigger);
    }, { signal });
    this.addEventListener('click', event => {
      if (event.target === this.dialog) { this.dialog.close(); return; }
      if (event.target.closest('[data-lead-close]')) this.dialog?.close();
      if (event.target.closest('[data-photo-button]')) this.track('visualization_upload_start');
      const remove = event.target.closest('[data-remove-photo]');
      if (remove) { this.files.splice(Number(remove.dataset.removePhoto), 1); this.renderPhotos(); }
    }, { signal });
    this.form.elements.unit?.addEventListener('change', () => { try { sessionStorage.setItem('tahho-unit', this.form.elements.unit.value); } catch {} }, { signal });
    this.form.elements.photos.addEventListener('change', () => {
      const incoming = [...this.form.elements.photos.files];
      const allowedTypes = ['image/jpeg','image/png','image/webp','image/heic','image/heif'];
      const allowedExtension = /\.(heic|heif|jpe?g|png|webp)$/i;
      const error = this.querySelector('[data-lead-error]');
      const typeUnknown = file => !file.type || file.type === 'application/octet-stream';
      const invalidType = file => !allowedTypes.includes(file.type) && !(typeUnknown(file) && allowedExtension.test(file.name));
      if (!this.campaign && this.files.length + incoming.length > 5) this.error(error.dataset.countError);
      else if (incoming.some(invalidType)) this.error(error.dataset.typeError);
      else if (incoming.some(file => file.size > 15 * 1024 * 1024)) this.error(error.dataset.sizeError);
      else if (incoming.some(file => file.size <= 0)) this.error(error.dataset.fileError);
      else if (incoming.length) { this.files = this.campaign ? incoming.slice(0, 1) : [...this.files, ...incoming]; this.renderPhotos(); this.track('visualization_photo_upload', { count: this.files.length }); }
      this.form.elements.photos.value = '';
    }, { signal });
    this.form.addEventListener('submit', event => { event.preventDefault(); this.submit(); }, { signal });
    this.followUpForm?.addEventListener('submit', event => { event.preventDefault(); this.submitFollowUp(); }, { signal });
    this.root.addEventListener('change', event => {
      const choice = event.target.closest('[data-visualization-product]');
      if (choice) this.selectProduct(choice);
    }, { signal });
    this.dialog?.addEventListener('close', () => this.opener?.focus({ preventScroll: true }), { signal });
  }
  disconnectedCallback() { this.controller?.abort(); this.urls?.forEach(url => URL.revokeObjectURL(url)); }
  selectProduct(choice) {
    const fields = this.form.elements;
    fields.product_id.value = choice.dataset.productId || '';
    fields.product_title.value = choice.dataset.productTitle || '';
    fields.product_url.value = choice.dataset.productUrl || '';
    fields.product_mode.value = choice.dataset.productMode || 'ready_to_buy';
    fields.selected_variant.value = choice.dataset.variantId || '';
    const trigger = this.root.querySelector('[data-lead="visualization"]');
    if (trigger) trigger.disabled = !fields.product_id.value || !fields.selected_variant.value;
    const status = this.root.querySelector('[data-product-selection-status]');
    if (status) status.textContent = choice.dataset.productTitle || '';
  }
  track(name, detail = {}) { this.dispatchEvent(new CustomEvent('tahho:analytics', { bubbles:true, detail:{ name, page_handle: this.form?.elements.page_handle?.value, campaign_id: this.form?.elements.campaign_id?.value, ...detail } })); }
  trackMetaLead() {
    if (!this.campaign || this.metaLeadTracked || typeof window.fbq !== 'function') return;
    try {
      const customerPrivacy = window.Shopify?.customerPrivacy;
      if (typeof customerPrivacy?.marketingAllowed === 'function' && !customerPrivacy.marketingAllowed()) return;
      this.metaLeadTracked = true;
      // Reuse Shopify's installed Meta Pixel. Do not initialize or configure another Pixel here.
      window.fbq('track', 'Lead');
    } catch { /* Conversion tracking must never interrupt a received request. */ }
  }
  open(trigger) {
    if (!this.dialog) return;
    if (this.busy) { if (!this.dialog.open) this.dialog.showModal(); return; }
    this.opener = trigger;
    const type = trigger.dataset.lead;
    const quote = type === 'custom_quote';
    if (this.dataset.standalone && (!this.form.elements.product_id.value || !this.form.elements.selected_variant.value)) return;
    try { const unit = sessionStorage.getItem('tahho-unit'); if (['in','ft','cm','m'].includes(unit)) this.form.elements.unit.value = unit; } catch {}
    this.form.elements.lead_type.value = type;
    const title = this.querySelector('[data-lead-title]');
    title.textContent = quote ? title.dataset.customQuote : title.dataset.visualization;
    const context = this.querySelector('[data-lead-context]');
    const productTitle = this.form.elements.product_title.value;
    context.textContent = quote ? context.dataset.customQuote : (context.dataset.visualizationTemplate || context.dataset.visualization).replace('[product]', productTitle);
    const submit = this.querySelector('[data-submit]');
    submit.dataset.idle = quote ? submit.dataset.customQuote : submit.dataset.visualization;
    submit.textContent = submit.dataset.idle;
    this.querySelector('[data-photo-required]').hidden = quote;
    this.form.elements.photos.required = !quote && this.files.length === 0;
    for (const key of ['width','height']) {
      const source = this.root.querySelector(`[data-dimension="${key}"]`);
      if (source?.value) this.form.elements[key].value = source.value;
    }
    const widthUnit = this.root.querySelector('[data-dimension="width-unit"]')?.value;
    const heightUnit = this.root.querySelector('[data-dimension="height-unit"]')?.value;
    if (widthUnit && heightUnit) {
      const meters = { in: 0.0254, ft: 0.3048, cm: 0.01, m: 1 };
      this.form.elements.unit.value = widthUnit;
      const height = Number(this.form.elements.height.value);
      if (height > 0) this.form.elements.height.value = Number((height * meters[heightUnit] / meters[widthUnit]).toFixed(6));
    }
    this.form.elements.width.required = quote;
    this.form.elements.height.required = quote;
    const variant = this.root.querySelector('product-form-component input[name="id"]');
    if (variant) this.form.elements.selected_variant.value = variant.value;
    if (!this.busy) {
      this.form.hidden = false;
      this.querySelector('[data-lead-success]').hidden = true;
    }
    this.dialog.showModal();
    this.querySelector('[data-lead-close]').focus();
    this.track(quote ? 'custom_quote_start' : 'visualization_start');
  }
  renderPhotos() {
    this.urls.forEach(url => URL.revokeObjectURL(url)); this.urls = [];
    const container = this.querySelector('[data-photo-previews]'); container.replaceChildren();
    this.files.forEach((file, index) => {
      const figure = document.createElement('figure');
      const url = URL.createObjectURL(file); this.urls.push(url);
      let preview;
      if (['image/heic', 'image/heif'].includes(file.type) || /\.(heic|heif)$/i.test(file.name)) {
        preview = document.createElement('span'); preview.className = 'tahho-photo-placeholder'; preview.textContent = file.name; preview.title = file.name;
      } else {
        preview = document.createElement('img'); preview.src = url; preview.alt = file.name;
      }
      figure.append(preview);
      if (!this.campaign) {
        const button = document.createElement('button'); button.type = 'button'; button.dataset.removePhoto = index;
        button.textContent = '✕'; button.setAttribute('aria-label', `${this.root.dataset.removePhotoLabel || 'Remove photo'} ${index + 1}`);
        figure.append(button);
      }
      container.append(figure);
    });
    this.form.elements.photos.required = this.form.elements.lead_type.value === 'visualization' && this.files.length === 0;
    const picker = this.querySelector('[data-photo-button]');
    picker.textContent = this.files.length ? picker.dataset.add : picker.dataset.choose;
    picker.hidden = this.files.length >= 5;
    this.querySelector('[data-lead-error]').hidden = true;
    this.classList.toggle('has-photo', this.files.length > 0);
    this.dispatchEvent(new CustomEvent('tahho:photos-change', { bubbles: true, detail: { count: this.files.length } }));
  }
  error(message, category = 'validation') { const error = this.querySelector('[data-lead-error]'); error.textContent = message; error.hidden = false; error.focus(); this.track('visualization_error', { error_code: category }); }
  async submit() {
    if (this.busy || !this.form.reportValidity()) return;
    const error = this.querySelector('[data-lead-error]');
    if (this.form.elements.lead_type.value === 'visualization' && !this.files.length) { this.error(error.dataset.requiredError || error.dataset.uploadError); return; }
    let endpoint;
    try { endpoint = new URL(this.dataset.endpoint, location.origin); } catch { this.error(error.dataset.submitError, 'endpoint'); return; }
    if (endpoint.origin !== location.origin || !endpoint.pathname.startsWith('/apps/')) { this.error(error.dataset.submitError); return; }
    this.busy = true;
    this.track('visualization_submit_attempt', { product_id: this.form.elements.product_id.value, photo_count: this.files.length });
    const button = this.querySelector('[data-submit]'); button.disabled = true; button.textContent = button.dataset.busy;
    const data = new FormData(this.form); data.delete('photos');
    const submittedType = this.form.elements.lead_type.value;
    const inputs = [...this.form.querySelectorAll('input, select, textarea, button')];
    inputs.forEach(input => { input.disabled = true; });
    this.files.forEach(file => data.append('photos', file));
    // Keep the key on a failed/ambiguous attempt so a retry cannot create a duplicate lead.
    this.requestId ||= crypto.randomUUID(); data.set('request_id', this.requestId);
    for (const [key, value] of Object.entries(this.attribution)) data.set(key, value);
    const progress = this.querySelector('[data-upload-progress]');
    if (progress) progress.hidden = false;
    try {
      const result = await this.transfer(endpoint, data);
      if (result.ok !== true || typeof result.lead_id !== 'string' || !result.lead_id) throw new Error('acknowledgement');
      const submittedNotes = this.form.elements.notes?.value.trim() || '';
      this.track(`${submittedType === 'custom_quote' ? 'custom_quote' : 'visualization'}_submit`, { lead_id: result.lead_id, product_id: this.form.elements.product_id.value });
      this.trackMetaLead();
      this.form.hidden = true;
      const successKey = submittedType === 'custom_quote' ? 'customQuote' : 'visualization';
      for (const element of this.querySelectorAll('[data-success-title], [data-success-product], [data-success-timing]')) {
        const value = element.dataset[`${successKey}Template`] || element.dataset[successKey];
        if (value) element.textContent = value.replace('[product]', this.form.elements.product_title.value);
      }
      const successProductName = this.querySelector('[data-success-product-name]');
      if (successProductName) successProductName.textContent = this.form.elements.product_title.value;
      const successGreeting = this.querySelector('[data-success-greeting]');
      if (successGreeting) {
        const name = this.form.elements.name?.value.trim();
        successGreeting.textContent = name ? `Hi ${name}, we've received your request.` : "Hi, we've received your request.";
      }
      this.configureFollowUp(result, submittedNotes);
      const success = this.querySelector('[data-lead-success]'); success.hidden = false; success.querySelector('[data-success-title]').focus();
      this.dispatchEvent(new CustomEvent('tahho:lead-success', { bubbles: true }));
      this.form.reset(); this.files = []; this.renderPhotos(); this.requestId = null;
    } catch (failure) { this.error(failure.message === 'http_429' ? 'Too many requests. Please try again in an hour.' : error.dataset.submitError, failure.message || 'network'); }
    finally { if (progress) progress.hidden = true; inputs.forEach(input => { input.disabled = false; }); this.busy = false; button.disabled = false; button.textContent = button.dataset.idle; }
  }
  configureFollowUp(result, submittedNotes) {
    if (!this.followUpForm) return;
    const available = this.campaign && !submittedNotes && typeof result.follow_up_token === 'string' && result.follow_up_token;
    this.followUpForm.hidden = !available;
    this.followUpForm.reset();
    this.followUpForm.elements.lead_id.value = available ? result.lead_id : '';
    this.followUpForm.elements.follow_up_token.value = available ? result.follow_up_token : '';
    const copy = this.querySelector('[data-follow-up-copy]'); if (copy) copy.hidden = !available;
    const saved = this.querySelector('[data-follow-up-saved]'); if (saved) saved.hidden = true;
    const error = this.querySelector('[data-follow-up-error]'); if (error) error.hidden = true;
  }
  async submitFollowUp() {
    if (!this.followUpForm || this.followUpBusy || !this.followUpForm.reportValidity()) return;
    let endpoint;
    try {
      endpoint = new URL(this.dataset.endpoint, location.origin);
      endpoint.pathname = `${endpoint.pathname.replace(/\/$/, '')}/follow-up`;
    } catch { return; }
    if (endpoint.origin !== location.origin || !endpoint.pathname.startsWith('/apps/')) return;
    this.followUpBusy = true;
    const button = this.followUpForm.querySelector('[data-follow-up-submit]');
    const error = this.querySelector('[data-follow-up-error]');
    button.disabled = true; button.textContent = button.dataset.busy;
    if (error) error.hidden = true;
    try {
      const result = await this.transfer(endpoint, new FormData(this.followUpForm));
      if (result.ok !== true) throw new Error('acknowledgement');
      this.followUpForm.hidden = true;
      const copy = this.querySelector('[data-follow-up-copy]'); if (copy) copy.hidden = true;
      const saved = this.querySelector('[data-follow-up-saved]');
      if (saved) { saved.hidden = false; saved.focus(); }
      this.track('visualization_follow_up_submit');
    } catch {
      if (error) { error.hidden = false; error.focus(); }
    } finally {
      this.followUpBusy = false; button.disabled = false; button.textContent = button.dataset.idle;
    }
  }
  transfer(endpoint, data) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', endpoint); xhr.timeout = 120000;
      xhr.upload.onprogress = event => {
        const progress = this.querySelector('[data-upload-progress] progress');
        const status = this.querySelector('[data-upload-status]');
        if (!progress || !event.lengthComputable) return;
        progress.value = Math.round(event.loaded / event.total * 100);
        if (status) status.textContent = progress.value === 100 ? 'Photo uploaded. Saving your request…' : `Uploading photo… ${progress.value}%`;
      };
      xhr.onerror = () => reject(new Error('network'));
      xhr.ontimeout = () => reject(new Error('timeout'));
      xhr.onabort = () => reject(new Error('aborted'));
      xhr.onload = () => {
        if (xhr.status < 200 || xhr.status >= 300) return reject(new Error(`http_${xhr.status}`));
        try { resolve(JSON.parse(xhr.responseText)); } catch { reject(new Error('acknowledgement')); }
      };
      xhr.send(data);
    });
  }
}
if (!customElements.get('tahho-lead-flow')) customElements.define('tahho-lead-flow', TahhoLeadFlow);
